// design-system-exempt: this page's colours are hard-coded ON PURPOSE. It exists to
// diagnose how iOS paints the top edge of the installed app, and a diagnostic that
// reads the theme it is diagnosing cannot tell you which of the two is wrong.
// A ruler for the installed app's top edge.
//
// Round one asked "which CSS property fixes the soft header" and got eight
// candidates and no answer. Round two and three each shipped a theory — match the
// colours across the edge, then make iOS commit to `viewport-fit=cover` and paint
// the strip ourselves — and each time the wordmark on the door came back soft. That
// is three guesses. The thing none of them established is the only thing that
// matters: WHERE the blur starts and stops, and whether the numbers the layout
// depends on are the numbers we think they are.
//
// So this page measures rather than proposes. It pins a ruler to the physical top of
// the window — `position: fixed`, so it escapes #root's padding and reads in raw
// viewport coordinates — and writes a line of text at every 12px from 0 to 144. One
// screenshot says which lines are soft, and the readout underneath says whether
// `env(safe-area-inset-top)` is a real number or the zero that would explain all of
// it.
//
// Reachable signed-out at /pwa-check (main.tsx routes it before the workspace
// match), so it works on the door, where the blur is most visible.
//
// ── ANSWERED (iOS 27, iPhone, 2026-09-17) ──────────────────────────────────
//
// `fixed` and `door` are sharp to row 0; `scroll` is soft. The effect attaches to the
// DOCUMENT scroller and to nothing else — the inner `overflow-y:auto` box in `fixed`
// sits at the same top edge and is not touched. It also LATCHES at load: `scroll`
// comes back sharp if you reach it by leaving `fixed`, so a real test needs a fresh
// load. The fix is in global.css: in standalone the document never scrolls and #root
// does. Keep this page — the next iOS release will move these numbers.
//
// ── the numbers it found on the way ─────────────────────────────────────────
//
//   safe-area-inset-top 0px · body::before height 0px · navigator.standalone true
//   screen.height 812 · window.innerHeight 762 · document scrolls true
//
// The window already starts 50px below the physical top, so there is no inset left
// to report and every rule keyed to `env(safe-area-inset-top)` is a no-op — which
// killed all three earlier theories at once. Rows at 0, 12 and 24 were soft; 36 was
// sharp. So the blur is a ~30px band at the top of the SCROLLING DOCUMENT, and the
// only move against it is to put nothing there. See `--ios-scroll-edge` in
// global.css. Keep this page: the next iOS release will move these numbers.

import { useEffect, useState } from 'react'

const STEP = 12
const ROWS = 13 // 0 → 144px

/**
 * **Change this word on every deploy that touches the top edge.**
 *
 * An installed app can serve a bundle several deploys behind — its own cookie jar,
 * its own cache, its own idea of when to check — and we have already spent one round
 * trip on "still fuzzy" that could not be told apart from "still on the old build".
 * A word a person can read off a screenshot settles that in one glance. `BUILD`
 * beside it is stamped by vite at build time (see vite.config.ts) and changes whether
 * or not anyone remembers to touch this one.
 */
const MARKER = 'CIVET'

declare const __BUILD__: string
const BUILD = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev'

/** Everything about the top edge that a layout here depends on. Read from the live
 *  document rather than assumed, because the assumption is what keeps being wrong. */
function readings() {
  const probe = document.createElement('div')
  probe.style.cssText =
    'position:fixed;top:0;left:0;height:env(safe-area-inset-top);width:1px;visibility:hidden'
  document.body.appendChild(probe)
  const inset = Math.round(probe.getBoundingClientRect().height)
  probe.remove()

  const band = getComputedStyle(document.body, '::before')
  const de = document.documentElement
  return {
    'build marker': `${MARKER} · ${BUILD}`,
    'safe-area-inset-top': `${inset}px`,
    'body::before height': band.height,
    'display-mode: standalone': String(window.matchMedia('(display-mode: standalone)').matches),
    'navigator.standalone': String(
      (navigator as Navigator & { standalone?: boolean }).standalone ?? 'undefined',
    ),
    'window.innerHeight': String(window.innerHeight),
    'screen.height': String(window.screen.height),
    'visualViewport.offsetTop': String(window.visualViewport?.offsetTop ?? 'n/a'),
    'document scrolls': String(de.scrollHeight > de.clientHeight),
    'html min-height': getComputedStyle(de).minHeight,
    '#root padding-top': getComputedStyle(document.getElementById('root')!).paddingTop,
    '#root position': getComputedStyle(document.getElementById('root')!).position,
    devicePixelRatio: String(window.devicePixelRatio),
  }
}

type Mode = 'scroll' | 'fixed' | 'door'

export function PwaCheck() {
  const [data, setData] = useState<Record<string, string> | null>(null)
  const [mode, setMode] = useState<Mode>('scroll')
  const door = mode === 'door'
  useEffect(() => setData(readings()), [])

  /**
   * The experiment, in one tap.
   *
   * Four observations say the blur follows the DOCUMENT scroller, not the status bar:
   * GT's `fixed inset-0` shells are sharp, this page's first version was a
   * non-scrolling `h-full` shell and was sharp, this version is 200dvh and is soft,
   * and the door stacks on a phone and is soft. `fixed` takes the root out of flow so
   * nothing scrolls but an inner box — if the ruler goes sharp, that is the answer and
   * the door already has the same shape. `door` sets the door's own class, so it tests
   * the shipped fix rather than a mock of it.
   */
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    html.classList.toggle('at-the-door', mode === 'door')
    const lock = mode !== 'scroll'
    html.style.overflow = lock ? 'hidden' : ''
    body.style.overflow = lock ? 'hidden' : ''
    html.style.height = lock ? '100%' : ''
    body.style.height = lock ? '100%' : ''
    const id = setTimeout(() => setData(readings()), 60)
    return () => {
      clearTimeout(id)
      html.classList.remove('at-the-door')
      html.style.overflow = ''
      body.style.overflow = ''
      html.style.height = ''
      body.style.height = ''
    }
  }, [mode])

  /**
   * Put this page in the door's shoes.
   *
   * The ruler above is SUPPOSED to be soft at the top — that is the measurement. What
   * it cannot tell us is whether the door's own fix reaches this device, because the
   * padding that carries it is keyed to a class only a Doorway sets. So set it here,
   * and reproduce what the door puts at the top: the same 32px section padding, the
   * same wordmark. If the sample below is sharp, the door is fixed and the soft ruler
   * rows are just the instrument doing its job.
   */
  useEffect(() => {
    const html = document.documentElement
    html.classList.toggle('at-the-door', door)
    setData(readings())
    return () => html.classList.remove('at-the-door')
  }, [door])

  return (
    <div
      style={{
        background: '#1d1813',
        color: '#f2eee4',
        ...(mode === 'scroll'
          ? { minHeight: '200dvh' }
          : // A fixed box the size of the window, with its own scroller inside: the
            // shape GT's app shells use and the one the door now takes in standalone.
            {
              position: 'fixed' as const,
              inset: 0,
              overflow: 'hidden' as const,
              display: 'flex',
              flexDirection: 'column' as const,
            }),
      }}
    >
      {/* Pinned to the window, not to #root: the whole question is what happens in
          the first hundred pixels of the window, so the ruler must not be pushed
          down by the very padding under test. Hidden while the door is simulated, or
          it sits on top of the sample it is there to help read. */}
      <div
        style={{
          position: 'fixed',
          inset: '0 0 auto 0',
          zIndex: 2147483647,
          display: door ? 'none' : 'block',
          // It is a ruler, not a surface: without this it swallows every tap in the
          // top 156px, including the button that switches the measurement.
          pointerEvents: 'none',
        }}
      >
        {Array.from({ length: ROWS }, (_, i) => i * STEP).map((y) => (
          <div
            key={y}
            style={{
              position: 'absolute',
              top: y,
              left: 0,
              right: 0,
              height: STEP,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: 'var(--mono, monospace)',
              fontSize: 'var(--text-micro)',
              lineHeight: '10px',
              color: '#f2eee4',
            }}
          >
            <span style={{ width: 18, height: 1, background: '#f2eee4' }} />
            <span style={{ width: 26 }}>{y}</span>
            {/* Fine strokes: a blur shows on hairlines long before it shows on body
                text, so the row says what it is AND carries a pattern that cannot
                survive being softened. */}
            <span>|||||| IS THIS ROW SHARP? {y}px</span>
          </div>
        ))}
      </div>

      {/* First thing in the flow, so it sits directly under whatever padding #root
          has — exactly where the door puts its wordmark. */}
      {/* Loud, and first: every screenshot of this page should say which build took
          it, without anyone having to ask. */}
      <div
        style={{
          position: 'fixed',
          right: 8,
          bottom: 8,
          zIndex: 2147483647,
          padding: '6px 10px',
          borderRadius: 'var(--radius-control)',
          background: '#a8501f',
          color: '#fff',
          fontFamily: 'var(--mono, monospace)',
          fontSize: 'var(--text-meta)',
          fontWeight: 700,
          letterSpacing: '0.08em',
        }}
      >
        {MARKER} · {BUILD}
      </div>

      <div
        className={door ? 'door-scroll' : undefined}
        style={
          mode === 'scroll'
            ? undefined
            : { flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'none' }
        }
      >
        <section style={{ paddingTop: 32, paddingLeft: 20, paddingBottom: 8 }}>
          <span style={{ fontSize: 'var(--text-lead)', fontWeight: 600, letterSpacing: '-0.01em' }}>
            Knowledge Futures · Crum
          </span>
          <div
            style={{
              fontFamily: 'var(--mono, monospace)',
              fontSize: 'var(--text-micro)',
              opacity: 0.6,
            }}
          >
            |||||| the door's wordmark, at the door's offset
          </div>
        </section>

        <div style={{ padding: `24px 20px 40px` }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {(['scroll', 'fixed', 'door'] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                style={{
                  flex: 1,
                  padding: '12px 8px',
                  borderRadius: 'var(--radius-control)',
                  border: '1px solid rgba(242,238,228,0.3)',
                  background: mode === m ? '#a8501f' : 'transparent',
                  color: '#f2eee4',
                  fontSize: 'var(--text-body)',
                  fontWeight: 600,
                }}
              >
                {m}
              </button>
            ))}
          </div>
          <h1 style={{ fontSize: 'var(--text-title)', fontWeight: 600, marginBottom: 4 }}>
            Top edge
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              opacity: 0.7,
              marginBottom: 18,
              lineHeight: 1.5,
            }}
          >
            The ruler is meant to be soft at the top — that is the measurement, not a bug. Tap the
            button, scroll back to the very top, and screenshot: the question is whether the
            wordmark line is sharp and whether #root padding-top reads 36px.
          </p>
          <div style={{ fontFamily: 'var(--mono, monospace)', fontSize: 'var(--text-meta)' }}>
            {data
              ? Object.entries(data).map(([k, v]) => (
                  <div
                    key={k}
                    style={{
                      display: 'flex',
                      gap: 10,
                      padding: '5px 0',
                      borderBottom: '1px solid rgba(242,238,228,0.14)',
                    }}
                  >
                    <span style={{ flex: 1, opacity: 0.66 }}>{k}</span>
                    <span style={{ fontWeight: 600 }}>{v}</span>
                  </div>
                ))
              : null}
          </div>
          <p
            style={{ fontSize: 'var(--text-meta)', opacity: 0.55, marginTop: 20, lineHeight: 1.5 }}
          >
            In <b>scroll</b> the document scrolls and the page is two screens tall. In <b>fixed</b>{' '}
            nothing scrolls but the box below the ruler. In <b>door</b> the door's own class is on,
            which is the shipped fix.
          </p>
        </div>
      </div>
    </div>
  )
}
